import { HttpClient } from '@angular/common/http'
import { Component, HostListener, Input, OnInit } from '@angular/core'
import { timeout } from 'rxjs/operators'

interface GoogleReview {
    id: string
    reviewerName: string
    rating: number
    text: string
    createdAt?: string | null
    profilePhotoUrl?: string | null
    reviewUrl?: string | null
    reviewerUrl?: string | null
}

interface GoogleReviewsResponse {
    businessName: string
    placeId?: string | null
    reviewsUrl?: string | null
    reviews: GoogleReview[]
    total: number
    totalReviewCount: number
    averageRating: number
}

@Component({
    selector: 'app-testimonials-block',
    templateUrl: './testimonials-block.component.html',
    styleUrls: ['./testimonials-block.component.scss'],
})
export class TestimonialsBlockComponent implements OnInit {
    @Input() header: string

    loading = true
    error = false

    businessName = ''
    placeId: string | null = null
    reviewsUrl = 'https://search.google.com/local/reviews?placeid=ChIJH46iFy1nzB0R0aZ8kLfHd1I'
    reviews: GoogleReview[] = []
    displayReviews: GoogleReview[] = []
    total = 0
    averageRating = 0
    writeReviewUrl = '#'

    private readonly displayMinRating = 4

    cardsPerView = 3
    currentPage = 0

    readonly starIndexes = [0, 1, 2, 3, 4]
    readonly Math = Math

    constructor(private http: HttpClient) {}

    ngOnInit(): void {
        this.updateCardsPerView()
        this.fetchReviews()
    }

    @HostListener('window:resize')
    onResize(): void {
        const previousCardsPerView = this.cardsPerView
        this.updateCardsPerView()
        if (this.cardsPerView !== previousCardsPerView) {
            this.currentPage = Math.min(this.currentPage, Math.max(0, this.pageCount - 1))
        }
    }

    private updateCardsPerView(): void {
        if (typeof window === 'undefined') {
            this.cardsPerView = 3
            return
        }
        const w = window.innerWidth
        if (w < 640) this.cardsPerView = 1
        else if (w < 1024) this.cardsPerView = 2
        else this.cardsPerView = 3
    }

    private fetchReviews(): void {
        this.http
            .get<GoogleReviewsResponse>('https://api-vjc23recia-ew.a.run.app/public/feeds/AmSoSIHnIEOAmJxb1hTM?limit=50')
            .pipe(timeout(15000))
            .subscribe({
                next: (resp) => {
                    this.businessName = resp.businessName
                    this.placeId = resp.placeId ?? null
                    this.reviewsUrl = resp.reviewsUrl || this.reviewsUrl
                    this.reviews = resp.reviews || []
                    this.displayReviews = this.reviews.filter((r) => r.rating >= this.displayMinRating)
                    this.total = resp.totalReviewCount ?? 0
                    this.averageRating = Math.round(resp.averageRating * 10) / 10
                    this.writeReviewUrl = this.placeId
                        ? `https://search.google.com/local/writereview?placeid=${this.placeId}`
                        : this.reviewsUrl || '#'
                    this.loading = false
                },
                error: () => {
                    this.error = true
                    this.loading = false
                },
            })
    }

    get pageCount(): number {
        if (this.displayReviews.length === 0) return 0
        return Math.ceil(this.displayReviews.length / this.cardsPerView)
    }

    get pages(): number[] {
        return Array.from({ length: this.pageCount }, (_, i) => i)
    }

    get trackTransform(): string {
        return `translateX(-${this.currentPage * 100}%)`
    }

    get cardWidth(): string {
        return `calc((100% - ${(this.cardsPerView - 1) * 16}px) / ${this.cardsPerView})`
    }

    prev(): void {
        if (this.currentPage > 0) this.currentPage -= 1
    }

    next(): void {
        if (this.currentPage < this.pageCount - 1) this.currentPage += 1
    }

    goTo(index: number): void {
        if (index >= 0 && index < this.pageCount) this.currentPage = index
    }

    initials(name: string): string {
        return (name || 'User')
            .split(' ')
            .filter(Boolean)
            .map((part) => part[0])
            .join('')
            .slice(0, 2)
            .toUpperCase()
    }

    relativeDate(iso?: string | null): string {
        if (!iso) return ''
        const date = new Date(iso)
        if (isNaN(date.getTime())) return ''
        const diffDays = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24))
        if (diffDays < 1) return 'Today'
        if (diffDays < 7) return `${diffDays} days ago`
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
        if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`
        return `${Math.floor(diffDays / 365)} years ago`
    }

    onAvatarError(event: Event): void {
        const img = event.target as HTMLImageElement
        img.style.display = 'none'
        const fallback = img.nextElementSibling as HTMLElement | null
        if (fallback) fallback.style.display = 'flex'
    }
}
